import React, { useEffect, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, formatRupiah } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Ticket } from "lucide-react";
import { toast } from "sonner";

export default function PromosPage() {
  const [promos, setPromos] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = async () => {
    const { data } = await api.get("/promos");
    setPromos(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Hapus promo "${p.code}"?`)) return;
    await api.delete(`/promos/${p.id}`);
    toast.success("Promo dihapus");
    load();
  };

  const toggleActive = async (p) => {
    await api.put(`/promos/${p.id}`, { ...p, active: !p.active });
    toast.success(p.active ? "Promo dinonaktifkan" : "Promo diaktifkan");
    load();
  };

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">Promo & Diskon</h1>
          <p className="text-[#5C6A61] mt-1">Buat kode promo persentase atau nominal yang bisa dipakai pelanggan di keranjang</p>
        </div>
        <button
          data-testid="btn-add-promo"
          onClick={() => setModal({ open: true, editing: null })}
          className="bg-[#C25934] text-white hover:bg-[#A94726] px-4 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Promo
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="promos-list">
        {promos.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#5C6A61] border border-dashed border-[#D3CEC4] rounded-md">
            Belum ada promo. Buat satu untuk mendorong pembelian.
          </div>
        )}
        {promos.map((p) => (
          <div key={p.id} className="bg-white rounded-md border border-[#D3CEC4] p-5" data-testid={`promo-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-[#C25934]" />
                  <span className="font-display text-lg font-semibold text-[#1E3A2B] tracking-wide">{p.code}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${p.active ? "bg-[#3A7D44]/10 text-[#3A7D44]" : "bg-[#D64045]/10 text-[#D64045]"}`}>
                    {p.active ? "Aktif" : "Non-aktif"}
                  </span>
                </div>
                <div className="text-sm text-[#1E3A2B] mt-1">
                  {p.type === "percent" ? `Diskon ${p.value}%` : `Potongan ${formatRupiah(p.value)}`}
                  {p.max_discount > 0 && p.type === "percent" && <span className="text-[#5C6A61]"> · maks {formatRupiah(p.max_discount)}</span>}
                </div>
                {p.min_order > 0 && <div className="text-xs text-[#5C6A61]">Minimum: {formatRupiah(p.min_order)}</div>}
                {p.note && <div className="text-xs text-[#5C6A61] italic mt-1">{p.note}</div>}
              </div>
              <div className="flex gap-1">
                <button data-testid={`btn-toggle-promo-${p.id}`} onClick={() => toggleActive(p)} className="p-2 hover:bg-[#F9F9F6] rounded-md text-xs text-[#5C6A61]">
                  {p.active ? "Pause" : "Aktifkan"}
                </button>
                <button data-testid={`btn-edit-promo-${p.id}`} onClick={() => setModal({ open: true, editing: p })} className="p-2 hover:bg-[#F9F9F6] rounded-md"><Pencil className="w-4 h-4 text-[#5C6A61]" /></button>
                <button data-testid={`btn-delete-promo-${p.id}`} onClick={() => del(p)} className="p-2 hover:bg-[#F9F9F6] rounded-md"><Trash2 className="w-4 h-4 text-[#D64045]" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <PromoModal
        open={modal.open}
        editing={modal.editing}
        onClose={() => setModal({ open: false, editing: null })}
        onSaved={() => { setModal({ open: false, editing: null }); load(); }}
      />
    </OwnerLayout>
  );
}

function PromoModal({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_order: 0, max_discount: 0, active: true, note: "" });

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...editing } : { code: "", type: "percent", value: 10, min_order: 0, max_discount: 0, active: true, note: "" });
    }
  }, [open, editing]);

  if (!open) return null;

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, code: form.code.toUpperCase().trim(), value: Number(form.value), min_order: Number(form.min_order), max_discount: Number(form.max_discount) };
    try {
      if (editing) await api.put(`/promos/${editing.id}`, payload);
      else await api.post("/promos", payload);
      toast.success("Promo disimpan");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="modal-promo">
      <div className="bg-white rounded-md max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold text-[#1E3A2B]">{editing ? "Edit Promo" : "Tambah Promo"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5C6A61]" /></button>
        </div>
        <form onSubmit={save} className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Kode Promo</span>
            <input data-testid="input-promo-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5 uppercase tracking-wide" placeholder="HEMAT10" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Tipe</span>
              <select data-testid="input-promo-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5">
                <option value="percent">Persen (%)</option>
                <option value="nominal">Nominal (Rp)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Nilai</span>
              <input data-testid="input-promo-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} required min={0} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Min. Pembelian</span>
              <input data-testid="input-promo-min" type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} min={0} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
            </label>
            {form.type === "percent" && (
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Maks. Diskon</span>
                <input data-testid="input-promo-max" type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} min={0} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" placeholder="0 = tanpa batas" />
              </label>
            )}
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Catatan (opsional)</span>
            <input data-testid="input-promo-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" placeholder="Berlaku untuk pelanggan baru" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="chk-promo-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Aktif
          </label>
          <button data-testid="btn-save-promo" type="submit" className="w-full bg-[#C25934] text-white py-2.5 rounded-xl font-medium">Simpan Promo</button>
        </form>
      </div>
    </div>
  );
}
